import { normalize } from "styled-normalize";
import styled, { createGlobalStyle, keyframes } from "styled-components";

import breakpoints from "../../../styles/breakpoints";
import { typography, transition, baseColors, fonts, header } from "../../../styles/variables";

const pageEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const PageTransition = styled.div`
  animation: ${pageEnter} 220ms ease-out;
  /* No will-change here, even though it would promote the element to
   * its own layer for the animation. will-change with the transform
   * value (and the running transform itself) makes the element a
   * containing block for position fixed descendants, which traps
   * every modal / tray inside this wrapper instead of anchoring them
   * to the viewport. The 220ms fade-in is short and runs once; not
   * worth the side-effect. */
`;

export const GlobalStyle = createGlobalStyle`
${normalize}

*, &:before, &:after {
  box-sizing: border-box;
}
html,
body {
  font-family: ${typography.baseFontFamily};
  font-size: ${typography.baseFontSize};
  margin: 0;
  padding: 0;
  min-height: 100%;
  color: ${baseColors.baseColor};
  line-height: ${typography.baseLineHeight};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  flex: 1;
}
body {
  overflow-x: hidden;
}
#root {
  display: flex;
  flex-direction: column;
  flex: 1;
}
header, footer {
  flex-shrink: 0;
}
.content {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  /* vh, not dvh — 'vh' is the modern stable large-viewport unit
   * (equivalent to 'lvh') and does not fluctuate as the mobile
   * browser URL bar shows/hides. 'dvh' does fluctuate, which causes
   * CLS on every scroll-triggered URL bar transition. svh is also
   * stable but resolves to the *small* viewport (URL bar visible),
   * which would shorten the page wrapper and let the footer creep
   * above the fold on initial load. */
  min-height: 100vh;
  /* Global safe-area padding so flow content starts below the inset
   * zone. The notch zone itself is painted by Layout's TopInsetSpacer,
   * whose colour comes from the useTopInsetBg() stack (Layout/context).
   * Page-leading elements register their preferred colour with the
   * stack instead of folding env() into their own padding. Inert on
   * web (env() = 0). */
  padding-top: env(safe-area-inset-top);
}
h1,
h2,
h3,
h4,
h5,
h6,
.h1,
.h2,
.h3,
.h4,
.h5,
.h6 {
  margin-top: 0;
  margin-bottom: 20px;
  font-family: ${typography.titleFontFamily}, sans-serif;
  line-height: ${typography.titleLineHeight};
  &:last-child {
    margin-bottom: 0;
  }
}

h1,
.h1 {
  font-size: 51px;
}
h2,
.h2 {
  font-size: 45px;
}
h3,
.h3 {
  font-size: 36px;
}
h4,
.h4 {
  font-size: 26px;
}
h5,
.h5 {
  font-size: 18px;
}
h6,
.h6 {
  font-size: 14px;
}

button,
a {
  transition: ${transition.baseTransition};
  cursor: pointer;
}

a {
  text-decoration: none;
  color: ${baseColors.baseLinkColor};
  &:hover,
  &:focus {
    color: ${baseColors.baseLinkHoverColor};
    text-decoration: none;
  }
}

button {
  border: none;
  background-color: transparent;
  &:hover, &:focus {
    outline: none !important;
  }
}

p, ul {
  margin-top: 0;
  margin-bottom: 20px;
  &:last-child {
    margin-bottom: 0;
  }
}

ul {
  padding: 0;
}

b, .bold {
  font-family: ${fonts.fontInterstateUltraBlack};
  text-transform: uppercase;
}
img {
  max-width: 100%;
  height: auto;
}
`;

/* Paints the safe-area inset zone (the notch / status bar overlay)
 * with the bg colour the active page or top-most mounted tray wants.
 * Default white; consumers override via useTopInsetBg(color) (see
 * Layout/context.ts).
 *
 * z-index has to sit above every overlay we own — OffCanvasTray
 * (6001), LogInPanel (9000), modals — otherwise the spacer is hidden
 * behind whatever opens and the notch falls back to whatever colour
 * the page underneath was painting. Trays can't reliably paint into
 * the notch themselves on Android WebView (overflow-scrolling +
 * position-fixed is unreliable), so the spacer is the one true
 * painter of the inset zone. pointer-events: none so it never blocks
 * input. Inert on web (env() = 0 → height: 0 → invisible). */
export const TopInsetSpacer = styled.div<{ $bg: string }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: env(safe-area-inset-top);
  background-color: ${(props) => props.$bg};
  z-index: 99999;
  pointer-events: none;
`;

export const BoxHolder = styled.div<{
  isHeaderShown: boolean;
}>`
  ${breakpoints.smDown} {
    position: sticky;
    left: 0;
    transition: ${transition.headerMenuBarTransition};
    top: ${(props) => (props.isHeaderShown ? header.mobileHeight : "0px")};
    z-index: 200;
  }
`;
